import React from "react";
import { loadFont as loadPoppins } from "@remotion/google-fonts/Poppins";
import { loadFont as loadRubik } from "@remotion/google-fonts/Rubik";
import { Audio, Sequence } from "remotion";
import { asset } from "./assets";
import { PUFFY } from "./icons.generated";

loadPoppins("normal", { weights: ["400", "500", "600", "700", "800"], subsets: ["latin"] });
loadRubik("normal", { weights: ["400", "500"], subsets: ["latin"] });

// stippl.io and the settled booking sheets
export const C = {
	ink: "#0f3e4a",
	green: "#13c892",
	greenDeep: "#11B484",
	greenText: "#0d8763",
	mint: "#EDF9F4",
	band: "#d6f0e2",
	line: "#C8EAD9",
	pink: "#e84d7e",
	pinkPale: "#FDE3EC",
	butter: "#FFF1CC",
	sky: "#DDF1F8",
	amber: "#ffc14a",
	blush: "#FDACC3",
	muted: "#58757b",
	paper: "#F9FCFD",
};

export const HEAD = "Poppins, sans-serif";
export const BODY = "Rubik, sans-serif";

// The tripmovie bundle compiles every .scss with src/css/General.scss prepended, and those rules are
// global: a body with line-height 1.5, letter-spacing 0.15px, antialiased font smoothing and Rubik.
// The film was made on the browser's defaults, and the artboards wrap their text differently with the
// extra spacing, so its root puts them back. (Box sizing needs nothing: Remotion itself already makes
// every element border-box, and General.scss's own * rule agrees.)
export const RESET_ATTR = { "data-feature-intro": "" };
export const RESET_CSS =
	"[data-feature-intro]{font:initial;letter-spacing:normal;color:initial;text-align:start;-webkit-font-smoothing:auto;-moz-osx-font-smoothing:auto}";

// The effect packs: the same cue names in four sounds, committed under public/featureintro/audio.
export const SFX_PACKS = ["sfx-hotel", "sfx-playful", "sfx-soft", "sfx-digital"] as const;
export type SfxPackName = (typeof SFX_PACKS)[number];

// which pack the <Sfx> cues play from
export const SfxPack = React.createContext<SfxPackName>("sfx-hotel");

// a film whose clock is remapped (a slowed hook, see ../booking/BookingIntroSlow.tsx) moves its cues with it
export const SfxWarp = React.createContext<(at: number) => number>((at) => at);

export const Sfx: React.FC<{ at: number; name: string; volume?: number }> = ({ at, name, volume = 0.7 }) => {
	const dir = React.useContext(SfxPack);
	const warp = React.useContext(SfxWarp);
	// Under a warped clock a film frame usually falls between two output frames, and the picture only
	// changes on the first output frame whose remapped frame has reached it: ceil(inv(at)). Rounding
	// played each slowed hook whoosh one frame before its cut (24 vs 25, 48/49, 72/73, 96/97). Unwarped
	// cues are whole numbers, so ceil changes nothing for them.
	// 90 frames is longer than the longest clip in any pack (sfx-hotel's chime, 2.4 s); at 60 the
	// booking-confirmed chime was cut off 2 s in while still audible.
	return (
		<Sequence from={Math.ceil(warp(at))} durationInFrames={90} layout="none">
			<Audio src={asset(`audio/${dir}/${name}.mp3`)} volume={volume} />
		</Sequence>
	);
};

export const Puffy: React.FC<{ name: string; size: number; color?: string; style?: React.CSSProperties }> = ({ name, size, color = C.ink, style }) => (
	<svg width={size} height={size} viewBox="0 0 32 32" style={style}>
		<path d={PUFFY[name]} fill={color} />
	</svg>
);

// Deterministic pseudo random
export const rnd = (seed: number) => {
	const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
	return x - Math.floor(x);
};
